import { useEffect, useRef } from 'react';

import { useIsMutating, useQueryClient } from '@tanstack/react-query';
import isEqual from 'lodash/isEqual';

import type { DashboardConfig } from '@/features/dashboard/api/dashboard.api';
import { dashboardOptions } from '@/features/dashboard/api/dashboard.options';
import { waitForDashboardConsistency } from '@/features/dashboard/api/dashboardReconciliation';
import type { DashboardTab } from '@/features/dashboard/types';

const CHANNEL_NAME = 'gx_dashboard_sync';

const isPlainRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && Object.getPrototypeOf(value) === Object.prototype;

const MARKET_APP_TYPES = new Set([
  'csvtable',
  'feed',
  'file_list',
  'graph',
  'minitable',
  'multi_group_table',
  'spot_history_table',
  'table',
  'tiles'
]);

const isWidget = (value: unknown): boolean =>
  isPlainRecord(value) &&
  typeof value.id === 'string' &&
  typeof value.type === 'string' &&
  MARKET_APP_TYPES.has(value.type);

const isMarketApp = (value: unknown): boolean =>
  isWidget(value) ||
  (isPlainRecord(value) &&
    typeof value.id === 'string' &&
    Array.isArray(value.tabs) &&
    value.tabs.every(isWidget));

const isMarketApps = (value: unknown): boolean => Array.isArray(value) && value.every(isMarketApp);

const isDashboardTabValue = (value: unknown): boolean =>
  isMarketApps(value) ||
  (isPlainRecord(value) && typeof value.id === 'string' && isMarketApps(value.content));

const isDashboardTab = (value: unknown): value is DashboardTab =>
  isPlainRecord(value) && Object.values(value).every(isDashboardTabValue);

const isDashboardConfig = (value: unknown): value is DashboardConfig =>
  isPlainRecord(value) && Object.values(value).every(isDashboardTab);

export type DashboardSyncMessage = {
  type: 'dashboard-cache-updated';
  dashboards: DashboardConfig;
  sentAt: number;
};

const isDashboardSyncMessage = (message: unknown): message is DashboardSyncMessage => {
  if (!isPlainRecord(message)) return false;

  const { type, dashboards, sentAt } = message;
  return (
    type === 'dashboard-cache-updated' &&
    isDashboardConfig(dashboards) &&
    typeof sentAt === 'number' &&
    Number.isFinite(sentAt)
  );
};

let lastBroadcastSentAt = 0;

const getNextBroadcastTimestamp = (): number => {
  lastBroadcastSentAt = Math.max(Date.now(), lastBroadcastSentAt + 1);
  return lastBroadcastSentAt;
};

const mergeConcurrentDashboardChanges = (
  settledDashboards: DashboardConfig | undefined,
  localDashboards: DashboardConfig,
  remoteDashboards: DashboardConfig
): DashboardConfig => {
  const mergedDashboards = { ...remoteDashboards };

  for (const [dashboardName, dashboard] of Object.entries(localDashboards)) {
    if (!settledDashboards || !isEqual(settledDashboards[dashboardName], dashboard)) {
      mergedDashboards[dashboardName] = dashboard;
    }
  }

  for (const dashboardName of Object.keys(settledDashboards ?? {})) {
    if (!(dashboardName in localDashboards)) delete mergedDashboards[dashboardName];
  }

  return mergedDashboards;
};

/**
 * Notifies other browser tabs that a dashboard mutation has occurred.
 * Opens a short-lived BroadcastChannel, posts the message, then closes it.
 * Confirmed snapshots let other tabs update their cache without a GET.
 */
export const broadcastDashboardMutation = (dashboards?: DashboardConfig): void => {
  const channel = new BroadcastChannel(CHANNEL_NAME);
  channel.postMessage(
    dashboards
      ? {
          type: 'dashboard-cache-updated',
          dashboards,
          sentAt: getNextBroadcastTimestamp()
        }
      : { type: 'dashboard-cache-invalidated' }
  );
  channel.close();
};

/**
 * Listens for dashboard mutations broadcast from other tabs and commits a
 * confirmed snapshot. Legacy and malformed messages use a refetch fallback.
 *
 * Call once in AppLayout (or any persistent authenticated component).
 */
export const useDashboardSync = (): void => {
  const queryClient = useQueryClient();
  const dashboardMutationCount = useIsMutating({
    mutationKey: dashboardOptions.mutations.all()
  });
  const dashboardMutationCountRef = useRef(dashboardMutationCount);
  const pendingRemoteRefreshRef = useRef(false);
  const pendingRemoteSentAtRef = useRef(0);
  const pendingRemoteSnapshotRef = useRef<DashboardConfig | undefined>(undefined);
  const latestAppliedSentAtRef = useRef(0);
  const settledDashboardsRef = useRef(
    queryClient.getQueryData<DashboardConfig>(dashboardOptions.all())
  );

  dashboardMutationCountRef.current = dashboardMutationCount;

  useEffect(() => {
    if (dashboardMutationCount > 0 || !pendingRemoteRefreshRef.current) return;

    pendingRemoteRefreshRef.current = false;
    const pendingRemoteSnapshot = pendingRemoteSnapshotRef.current;
    const pendingRemoteSentAt = pendingRemoteSentAtRef.current;
    pendingRemoteSnapshotRef.current = undefined;
    latestAppliedSentAtRef.current = Math.max(latestAppliedSentAtRef.current, pendingRemoteSentAt);
    pendingRemoteSentAtRef.current = 0;

    if (pendingRemoteSnapshot) {
      const localDashboards =
        queryClient.getQueryData<DashboardConfig>(dashboardOptions.all()) ?? {};
      const expectedDashboards = mergeConcurrentDashboardChanges(
        settledDashboardsRef.current,
        localDashboards,
        pendingRemoteSnapshot
      );
      const controller = new AbortController();

      waitForDashboardConsistency(
        { type: 'replace', dashboards: expectedDashboards },
        { signal: controller.signal }
      )
        .then(dashboards => {
          if (latestAppliedSentAtRef.current === pendingRemoteSentAt) {
            queryClient.setQueryData(dashboardOptions.all(), dashboards);
          }
        })
        .catch(() => undefined);

      return () => controller.abort();
    }

    queryClient
      .refetchQueries({ queryKey: dashboardOptions.all(), type: 'active' })
      .catch(() => undefined);
  }, [dashboardMutationCount, queryClient]);

  useEffect(
    () =>
      queryClient.getQueryCache().subscribe(event => {
        if (
          event.type !== 'updated' ||
          !isEqual(event.query.queryKey, dashboardOptions.all()) ||
          queryClient.isMutating({ mutationKey: dashboardOptions.mutations.all() }) > 0
        ) {
          return;
        }

        settledDashboardsRef.current = event.query.state.data as DashboardConfig | undefined;
      }),
    [queryClient]
  );

  useEffect(() => {
    const channel = new BroadcastChannel(CHANNEL_NAME);

    channel.onmessage = event => {
      // A server read during an optimistic write could overwrite the local
      // mutation. Record the remote change and reconcile from the server once
      // all dashboard mutations in this tab have settled.
      if (dashboardMutationCountRef.current > 0) {
        pendingRemoteRefreshRef.current = true;
        if (isDashboardSyncMessage(event.data)) {
          if (event.data.sentAt > pendingRemoteSentAtRef.current) {
            pendingRemoteSentAtRef.current = event.data.sentAt;
            pendingRemoteSnapshotRef.current = event.data.dashboards;
          }
        }
        return;
      }

      if (isDashboardSyncMessage(event.data)) {
        if (event.data.sentAt <= latestAppliedSentAtRef.current) return;

        latestAppliedSentAtRef.current = event.data.sentAt;
        queryClient.setQueryData(dashboardOptions.all(), event.data.dashboards);
        return;
      }

      queryClient
        .refetchQueries({ queryKey: dashboardOptions.all(), type: 'active' })
        .catch(() => undefined);
    };

    return () => {
      channel.close();
    };
  }, [queryClient]);
};
